import { createEmptyHistoryState } from "@lexical/react/LexicalHistoryPlugin";
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import useAuth from "../hooks/useAuth";

import { hydrateImagePaths } from "./utils/serializeImages";

import toast from 'react-simple-toasts';
import 'react-simple-toasts/dist/theme/success.css';
import 'react-simple-toasts/dist/theme/failure.css';
import theme from "./themes/NEDTheme";


const PageOrientations = {
    portrait: "portrait",
    landscape: "landscape",
} as const;
type PageOrientation = (typeof PageOrientations)[keyof typeof PageOrientations];

export const EditorModes = {
    scale: {
        name: "scale",
        label: "Scale",
    },
    simple: {
        name: "simple",
        label: "Simple",
    },
} as const;
export type EditorMode = (typeof EditorModes)[keyof typeof EditorModes];

export const IndentClassnames = {
    portrait: "NEDTheme__indentPortrait",
    landscape: "NEDTheme__indentLandscape",
    simple: "NEDTheme__indentSimple",
} as const;

interface EditorContextProps {
    isLinkEditMode: boolean;
    setIsLinkEditMode: React.Dispatch<React.SetStateAction<boolean>>;
    pageOrientation: PageOrientation;
    setPageOrientation: React.Dispatch<React.SetStateAction<PageOrientation>>;
    pageWidth: number;
    handleWheel: (event: WheelEvent) => void;
    zoomLevel: number;
    totalDivisions?: number;
    sharedEditorHistory: {
        historyState: ReturnType<typeof createEmptyHistoryState>;
    };
    setPieceId: React.Dispatch<React.SetStateAction<number | null>>;
    editorState: React.MutableRefObject<any>;
    initialEditorState: string | null;
    isnitialEditorStateLoading: boolean;
    onSave: (content: FormData) => void;
    editorMode: EditorMode;
    setEditorMode: React.Dispatch<React.SetStateAction<EditorMode>>;
    simplePageWidth: string;
    setSimplePageWidth: React.Dispatch<React.SetStateAction<string>>;
}

const EditorContext = createContext({} as EditorContextProps);

const Provider = EditorContext.Provider;

export const useEditorContext = () => useContext(EditorContext);

// const MIN_PAGE_WIDTH = 25;
// export const INIT_PAGE_WIDTH = 75;
// const MAX_PAGE_WIDTH = 97.8;
// export const RULER_WIDTH = 100 - MAX_PAGE_WIDTH;
const MIN_PAGE_WIDTH = 500;
export const INIT_PAGE_WIDTH = 1000;
const MAX_PAGE_WIDTH = 1500;
export const RULER_WIDTH = 2.2; //vw

const MIN_ZOOM_LEVEL = MIN_PAGE_WIDTH / INIT_PAGE_WIDTH;
const MAX_ZOOM_LEVEL = MAX_PAGE_WIDTH / INIT_PAGE_WIDTH;

const INIT_SIMPLE_PAGE_WIDTH = "36";

export const EditorProvider = ({ children }: { children: React.ReactNode }) => {
    const [pieceId, setPieceId] = useState<number | null>(null);
    const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);
    const editorState = useRef<any>(null);
    const [initialEditorState, setInitialEditorState] = useState<string | null>(
        null,
    );
    const [isnitialEditorStateLoading, setIsInitialEditorStateLoading] =
        useState<boolean>(true);

    const [zoomLevel, setZoomLevel] = useState<number>(1);
    const [pageWidth, setPageWidth] = useState<number>(INIT_PAGE_WIDTH);
    const [pageOrientation, setPageOrientation] =
        useState<PageOrientation>("portrait");
    const [editorMode, setEditorMode] = useState<EditorMode>(EditorModes.scale);
    const [simplePageWidth, setSimplePageWidth] = useState<string>(
        INIT_SIMPLE_PAGE_WIDTH,
    ); // % of page width
    const { api } = useAuth();

    useEffect(() => {
        setPageWidth(INIT_PAGE_WIDTH * zoomLevel);
    }, [zoomLevel]);

    var totalDivisions = useMemo(() => {
        if (pageOrientation === "portrait") {
            return Math.floor((210 / 10) * 4);
        }
        return Math.floor((297 / 10) * 4);
    }, [pageOrientation]);

    const handleWheel = useCallback((event: WheelEvent) => {
        if (event.ctrlKey) {
            event.preventDefault(); // Prevent the default browser zoom/scroll

            const delta = event.deltaY;
            setZoomLevel((prevZoom) => {
                // Adjust zoom level based on the wheel event delta
                return delta < 0
                    ? Math.min(prevZoom + 0.1, MAX_ZOOM_LEVEL)
                    : Math.max(prevZoom - 0.1, MIN_ZOOM_LEVEL);
            });
        }
    }, []);

    const sharedEditorHistory = useMemo(
        () => ({ historyState: createEmptyHistoryState() }),
        [],
    );

    const loadInititalEditorState = useCallback(() => {
        if (!pieceId || initialEditorState !== null) return;
        // setIsInitialEditorStateLoading(true);
        api.getPiece(pieceId, true)
            .then((res) => {
                editorState.current = res.data.content;
                const content = hydrateImagePaths(res.data.content);
                try {
                    const json = JSON.parse(content);
                    if ('editorMode' in json) {
                      switch (json.editorMode) {
                        case 'scale':
                          setEditorMode(EditorModes.scale);
                          break;
                        case 'simple':
                          setEditorMode(EditorModes.simple);
                          setSimplePageWidth(json.simplePageWidth);
                          break;
                      }
                      setInitialEditorState(JSON.stringify(json.editorState));
                    } else setInitialEditorState(content);
                } catch (err) {
                    // leave null
                }
                setIsInitialEditorStateLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setIsInitialEditorStateLoading(false);
            });
    }, [pieceId, api, initialEditorState]);

    useEffect(() => {
        loadInititalEditorState();
    }, [loadInititalEditorState]);

    const onSave = useCallback(
        (content: FormData) => {
            if (!pieceId) return;
            api.updatePieceContent(pieceId, content)
                .then((res) => {
                    // console.log("saved data", res.data.content);
                    toast("Saved successfully", { theme: 'success', duration: 3000 });
                })
                .catch((err) => {
                    console.error(err);
                    toast("Failed to save", { theme: 'failure', duration: 3000 });
                });
        },
        [pieceId, api],
    );

    return (
        <Provider
            value={{
                isLinkEditMode,
                setIsLinkEditMode,
                pageOrientation,
                setPageOrientation,
                pageWidth,
                handleWheel,
                zoomLevel,
                totalDivisions,
                sharedEditorHistory,
                setPieceId,
                editorState,
                initialEditorState,
                isnitialEditorStateLoading,
                onSave,
                editorMode,
                setEditorMode,
                simplePageWidth,
                setSimplePageWidth,
            }}
        >
            {children}
        </Provider>
    );
};
